/* src/modules/it/components/ITRequestPortal.js */
import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, addDoc, query, where, orderBy, onSnapshot, 
    doc, updateDoc, serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { 
    PlusIcon, SearchIcon, ChevronLeftIcon, 
    ChevronRightIcon, UserIcon, PaperAirplaneIcon, 
    ExclamationCircleIcon, ShoppingBagIcon
} from '../../../components/icons/Icons';

// --- CONSTANTS ---
const INVENTORY_CATEGORIES = [
    "Laptop (Leased)", "Laptop (Owned)", "Monitor", "Headset", 
    "Keyboard", "Speaker", "Mouse", "Webcam", "Tablet", 
    "License / SaaS", "Other"
];

const ISSUE_CATEGORIES = [
    "Hardware Fault", "Software Issue", "Network/Internet", 
    "Access/Permissions", "Email/Account", "Other"
];

// --- HELPERS ---
const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    return new Date(timestamp.seconds * 1000).toLocaleDateString() + ' ' + 
           new Date(timestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const getStatusColor = (status) => {
    switch(status) {
        case 'Open': return 'bg-blue-100 text-blue-800';
        case 'In Progress': return 'bg-purple-100 text-purple-800';
        case 'Resolved': return 'bg-green-100 text-green-800';
        case 'Closed': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-600';
    }
};

const UserAvatar = ({ url, name, className = "h-8 w-8" }) => {
    const [imgError, setImgError] = useState(false);
    if (url && !imgError) {
        return <img src={url} alt={name} className={`${className} rounded-full object-cover border border-gray-200 shrink-0`} onError={() => setImgError(true)} />;
    }
    return (
        <div className={`${className} rounded-full bg-blue-50 flex items-center justify-center text-[#213368] font-bold text-xs shrink-0 border border-blue-100`}>
            {name ? name.charAt(0).toUpperCase() : <UserIcon className="h-4 w-4" />}
        </div>
    );
};

export default function ITRequestPortal({ userProfile, appId, isITAdmin }) {
    // View State
    const [viewMode, setViewMode] = useState(isITAdmin ? 'all' : 'my'); 
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Data State
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter & Sort State
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('Open'); 
    const [typeFilter, setTypeFilter] = useState('All');
    
    // Sort Configuration
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // --- 1. FETCH DATA ---
    useEffect(() => {
        setLoading(true);
        const ref = collection(db, `artifacts/${appId}/it_requests`);
        
        let constraints = [orderBy('createdAt', 'desc')];

        if (viewMode === 'my') {
            constraints.push(where('requesterId', '==', userProfile.uid));
        }

        const q = query(ref, ...constraints);
        
        const unsub = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setTickets(data);
            setLoading(false);
        });

        return () => unsub();
    }, [appId, viewMode, userProfile.uid]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, statusFilter, typeFilter, viewMode]);

    // --- 2. FILTER & SORT LOGIC ---
    const processedTickets = useMemo(() => {
        // 1. Filter
        let filtered = tickets.filter(t => {
            const q = searchQuery.toLowerCase();
            const matchesSearch = !q || 
                t.subject.toLowerCase().includes(q) || 
                t.requestNumber?.toLowerCase().includes(q) ||
                t.requesterName.toLowerCase().includes(q);
            
            let matchesStatus = true;
            if (statusFilter === 'Open') matchesStatus = ['Open', 'In Progress'].includes(t.status);
            else if (statusFilter === 'Closed') matchesStatus = ['Resolved', 'Closed'].includes(t.status);
            
            const matchesType = typeFilter === 'All' || t.type === typeFilter;

            return matchesSearch && matchesStatus && matchesType;
        });

        // 2. Sort (Client-Side)
        return filtered.sort((a, b) => {
            if (!sortConfig.key) return 0;

            const aVal = a[sortConfig.key] || '';
            const bVal = b[sortConfig.key] || '';

            if (sortConfig.key === 'createdAt') {
                const aTime = a.createdAt?.seconds || 0;
                const bTime = b.createdAt?.seconds || 0;
                return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

    }, [tickets, searchQuery, statusFilter, typeFilter, sortConfig]);

    // --- 3. PAGINATION ---
    const totalPages = Math.ceil(processedTickets.length / itemsPerPage);
    const paginatedTickets = processedTickets.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // --- RENDER ---
    
    if (selectedTicketId) {
        const ticket = tickets.find(t => t.id === selectedTicketId);
        return (
            <TicketConversation 
                ticket={ticket} 
                appId={appId} 
                userProfile={userProfile} 
                isITAdmin={isITAdmin} 
                onBack={() => setSelectedTicketId(null)} 
            />
        );
    }

    return (
        <div className="space-y-6">
            {/* Top Bar: Tabs & Actions */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-b border-gray-200 pb-4">
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                    <button 
                        onClick={() => setViewMode('my')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${viewMode === 'my' ? 'bg-white shadow-sm text-[#213368]' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        My Requests
                    </button>
                    {isITAdmin && (
                        <button 
                            onClick={() => setViewMode('all')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${viewMode === 'all' ? 'bg-white shadow-sm text-[#213368]' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            All Requests
                        </button>
                    )}
                </div>

                <button 
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-[#20BEC6] hover:bg-[#1ba8af] text-white rounded-lg shadow-sm font-medium text-sm"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    New Request
                </button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative md:col-span-2">
                    <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Search subject, ID, or user..." 
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none"
                    />
                </div>
                <select 
                    value={statusFilter} 
                    onChange={e => setStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                >
                    <option value="Open">Active (Open/In Prog)</option>
                    <option value="Closed">Completed</option>
                    <option value="All">All Statuses</option>
                </select>
                <select 
                    value={typeFilter} 
                    onChange={e => setTypeFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                >
                    <option value="All">All Types</option>
                    <option value="Requisition">New Requisition</option>
                    <option value="Issue">IT Issue</option>
                </select>
            </div>

            {/* Ticket List Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b select-none">
                            <tr>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('subject')}>
                                    Ticket {sortConfig.key === 'subject' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                                </th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('category')}>
                                    Category {sortConfig.key === 'category' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                                </th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                                    Status {sortConfig.key === 'status' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                                </th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('requesterName')}>
                                    Requester {sortConfig.key === 'requesterName' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                                </th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdAt')}>
                                    Created {sortConfig.key === 'createdAt' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="p-8 text-center">Loading...</td></tr>
                            ) : paginatedTickets.length === 0 ? (
                                <tr><td colSpan="5" className="p-8 text-center text-gray-400">No requests found.</td></tr>
                            ) : (
                                paginatedTickets.map((ticket) => (
                                    <tr 
                                        key={ticket.id} 
                                        onClick={() => setSelectedTicketId(ticket.id)}
                                        className="bg-white border-b hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-start gap-3">
                                                <div className={`p-2 rounded-lg shrink-0 ${ticket.type === 'Requisition' ? 'bg-purple-50 text-purple-600' : 'bg-orange-50 text-orange-600'}`}>
                                                    {ticket.type === 'Requisition' ? <ShoppingBagIcon className="w-5 h-5"/> : <ExclamationCircleIcon className="w-5 h-5"/>}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-[#213368] text-base">{ticket.subject}</div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        <span className="font-mono bg-gray-100 px-1 rounded">#{ticket.requestNumber || 'ID'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900 font-medium">{ticket.category}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                                                {ticket.status}
                                            </span>
                                            {ticket.type === 'Requisition' && ticket.dateNeeded && (
                                                <div className="text-[10px] text-gray-500 mt-1">
                                                    By: {new Date(ticket.dateNeeded).toLocaleDateString()}
                                                </div>
                                            )}
                                            {ticket.expectedResolutionDate && ticket.status !== 'Closed' && ticket.status !== 'Resolved' && (
                                                <div className="text-[10px] text-gray-500 mt-1">
                                                    ETA: {new Date(ticket.expectedResolutionDate).toLocaleDateString()}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <UserAvatar name={ticket.requesterName} className="h-8 w-8 mr-2" />
                                                <div>
                                                    <div className="font-medium text-gray-900">{ticket.requesterName}</div>
                                                    <div className="text-xs text-gray-400">{ticket.requesterDept || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {formatDate(ticket.createdAt)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Pagination */}
                {processedTickets.length > 0 && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                            Page {currentPage} of {totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(c => Math.max(1, c - 1))} disabled={currentPage === 1} className="p-1 rounded bg-white border disabled:opacity-50"><ChevronLeftIcon className="w-4 h-4"/></button>
                            <button onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))} disabled={currentPage === totalPages} className="p-1 rounded bg-white border disabled:opacity-50"><ChevronRightIcon className="w-4 h-4"/></button>
                        </div>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {isCreateModalOpen && (
                <CreateRequestModal 
                    onClose={() => setIsCreateModalOpen(false)} 
                    appId={appId} 
                    userProfile={userProfile} 
                />
            )}
        </div>
    );
}

function CreateRequestModal({ onClose, appId, userProfile }) {
    const [type, setType] = useState('Issue');
    const [formData, setFormData] = useState({
        category: '', subject: '', description: '',
        dateNeeded: '', beneficiary: 'Self'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const categories = type === 'Issue' ? ISSUE_CATEGORIES : INVENTORY_CATEGORIES;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const ticketData = {
                type,
                ...formData,
                status: 'Open',
                requesterId: userProfile.uid,
                requesterName: userProfile.name,
                requesterEmail: userProfile.email,
                requesterDept: userProfile.department || 'Unassigned',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                commentCount: 0,
                requestNumber: `REQ-${Math.floor(1000 + Math.random() * 9000)}` 
            };
            
            if (type === 'Issue') {
                delete ticketData.dateNeeded;
                delete ticketData.beneficiary;
            }

            await addDoc(collection(db, `artifacts/${appId}/it_requests`), ticketData);
            onClose();
        } catch (error) {
            console.error("Error creating ticket:", error);
            alert("Failed to create request.");
        }
        setIsSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                <div className="bg-[#213368] p-4 flex justify-between items-center text-white">
                    <h3 className="font-bold text-lg">Create IT Request</h3>
                    <button onClick={onClose} className="hover:text-gray-300">✕</button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <button type="button" onClick={() => setType('Issue')} className={`p-3 rounded-lg border-2 text-center transition-all ${type === 'Issue' ? 'border-orange-500 bg-orange-50 text-orange-700 font-bold' : 'border-gray-200 text-gray-500'}`}>
                            <ExclamationCircleIcon className="w-6 h-6 mx-auto mb-1"/> Report Issue
                        </button>
                        <button type="button" onClick={() => setType('Requisition')} className={`p-3 rounded-lg border-2 text-center transition-all ${type === 'Requisition' ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'border-gray-200 text-gray-500'}`}>
                            <ShoppingBagIcon className="w-6 h-6 mx-auto mb-1"/> New Requisition
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Category</label>
                            <select required className="w-full border border-gray-300 rounded-lg p-2 text-sm" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                                <option value="">Select Category</option>
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        {type === 'Requisition' && (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date Needed By</label>
                                <input type="date" required className="w-full border border-gray-300 rounded-lg p-2 text-sm" value={formData.dateNeeded} onChange={e => setFormData({...formData, dateNeeded: e.target.value})} />
                            </div>
                        )}
                    </div>

                    {type === 'Requisition' && (
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Who is this for?</label>
                            <input type="text" placeholder="E.g. Self, New Hire, Dept Shared..." className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" value={formData.beneficiary} onChange={e => setFormData({...formData, beneficiary: e.target.value})} />
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Subject</label>
                        <input required type="text" className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" placeholder="Brief summary..." value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
                        <textarea required rows="4" className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" placeholder="Please provide detailed information..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                    </div>

                    <div className="flex justify-end pt-2">
                        <button type="submit" disabled={isSubmitting} className="bg-[#213368] text-white px-6 py-2 rounded-lg font-bold hover:bg-opacity-90 disabled:opacity-50">{isSubmitting ? 'Submitting...' : 'Submit Request'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function TicketConversation({ ticket, appId, userProfile, isITAdmin, onBack }) {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [expectedDate, setExpectedDate] = useState(ticket.expectedResolutionDate || '');

    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/it_requests/${ticket.id}/comments`), orderBy('createdAt', 'asc'));
        const unsub = onSnapshot(q, (snap) => {
            setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [appId, ticket.id]);

    const handleSendComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        const isRequester = userProfile.uid === ticket.requesterId;
        const roleLabel = isRequester ? 'User' : 'IT Support';

        await addDoc(collection(db, `artifacts/${appId}/it_requests/${ticket.id}/comments`), {
            content: newComment,
            authorId: userProfile.uid,
            authorName: userProfile.name,
            authorRole: roleLabel,
            createdAt: serverTimestamp()
        });
        setNewComment('');
    };

    const handleUpdateStatus = async (newStatus) => {
        if (!window.confirm(`Change status to ${newStatus}?`)) return;
        await updateDoc(doc(db, `artifacts/${appId}/it_requests`, ticket.id), { status: newStatus, updatedAt: serverTimestamp() });
        await addDoc(collection(db, `artifacts/${appId}/it_requests/${ticket.id}/comments`), { content: `changed status to ${newStatus}`, isSystemMsg: true, authorName: userProfile.name, createdAt: serverTimestamp() });
    };

    const handleDateChange = async (e) => {
        const dateVal = e.target.value;
        setExpectedDate(dateVal);
        await updateDoc(doc(db, `artifacts/${appId}/it_requests`, ticket.id), { expectedResolutionDate: dateVal });
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-[calc(100vh-200px)] animate-slideIn">
            <div className="p-4 border-b border-gray-200 flex justify-between items-start bg-gray-50 rounded-t-xl">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="p-2 hover:bg-gray-200 rounded-full transition"><ChevronLeftIcon className="w-5 h-5 text-gray-600"/></button>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-xs bg-gray-200 px-1.5 py-0.5 rounded text-gray-700">#{ticket.requestNumber}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${getStatusColor(ticket.status)}`}>{ticket.status}</span>
                            {ticket.type === 'Requisition' && ticket.dateNeeded && <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded border border-purple-200">Needed by: {ticket.dateNeeded}</span>}
                        </div>
                        {/* WRAPPING FIX 1: Allow header to break words */}
                        <h2 className="text-xl font-bold text-[#213368] mt-1 break-words">{ticket.subject}</h2>
                    </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                    {isITAdmin && (
                        <div className="flex items-center text-xs">
                            <span className="text-gray-500 mr-2">Expected Resolve:</span>
                            <input type="date" className="border border-gray-300 rounded px-2 py-1 bg-white" value={expectedDate} onChange={handleDateChange} />
                        </div>
                    )}
                    {isITAdmin && ticket.status !== 'Closed' && (
                        <div className="flex gap-2">
                            {ticket.status === 'Open' && <button onClick={() => handleUpdateStatus('In Progress')} className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded hover:bg-purple-200">Start Work</button>}
                            {ticket.status !== 'Resolved' && <button onClick={() => handleUpdateStatus('Resolved')} className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded hover:bg-green-200">Resolve</button>}
                            <button onClick={() => handleUpdateStatus('Closed')} className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-bold rounded hover:bg-gray-300">Close</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/30">
                        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex justify-between items-start mb-3 border-b border-gray-100 pb-2">
                                <div className="flex items-center gap-2">
                                    <UserAvatar name={ticket.requesterName} className="h-8 w-8"/>
                                    <div>
                                        <p className="text-sm font-bold text-gray-900">{ticket.requesterName}</p>
                                        <p className="text-xs text-gray-500">{ticket.requesterDept} • {formatDate(ticket.createdAt)}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">{ticket.type}</span>
                                    <span className="block text-xs font-medium text-blue-600">{ticket.category}</span>
                                    {ticket.beneficiary && <span className="block text-[10px] text-gray-500 mt-1">For: {ticket.beneficiary}</span>}
                                </div>
                            </div>
                            {/* WRAPPING FIX 2: Added break-all and w-full */}
                            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed break-words break-all w-full">{ticket.description}</p>
                        </div>

                        {comments.map(c => {
                            const isMe = c.authorId === userProfile.uid;
                            const isSystem = c.isSystemMsg;
                            const isSupportMsg = c.authorRole === 'IT Support';

                            return (
                                <div key={c.id} className={`flex ${isSystem ? 'justify-center' : (isMe ? 'justify-end' : 'justify-start')}`}>
                                    {isSystem ? (
                                        <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                                            {c.authorName} {c.content} • {formatDate(c.createdAt)}
                                        </span>
                                    ) : (
                                        <div className={`flex gap-2 max-w-[85%] ${isMe ? 'flex-row-reverse' : ''}`}>
                                            <UserAvatar name={c.authorName} className="h-8 w-8 mt-1"/>
                                            <div className={`p-3 rounded-lg shadow-sm border min-w-0 ${isSupportMsg ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-200'}`}>
                                                <div className={`flex items-baseline gap-2 mb-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                                    <span className={`text-xs font-bold ${isSupportMsg ? 'text-[#20BEC6]' : 'text-gray-700'}`}>{c.authorName} {isSupportMsg && '(Support)'}</span>
                                                    <span className="text-[10px] text-gray-400">{formatDate(c.createdAt)}</span>
                                                </div>
                                                {/* WRAPPING FIX 3: Added break-all to comments */}
                                                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words break-all">{c.content}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {ticket.status !== 'Closed' ? (
                        <div className="p-4 bg-white border-t border-gray-200">
                            <form onSubmit={handleSendComment} className="flex gap-2">
                                <input type="text" value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Type your reply..." className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                <button type="submit" disabled={!newComment.trim()} className="bg-[#213368] text-white p-2 rounded-full hover:opacity-90 disabled:opacity-50">
                                    <PaperAirplaneIcon className="w-5 h-5 translate-x-0.5" />
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div className="p-4 bg-gray-50 border-t border-gray-200 text-center text-sm text-gray-500 italic">This ticket is closed.</div>
                    )}
                </div>
            </div>
        </div>
    );
}